// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import FieldDefinition, { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import { TabDefinition } from "@insite/client-framework/Types/TabDefinition";

interface TabsDefinition {
    org: TabDefinition;
    advanced: TabDefinition;
}

const tabs: TabsDefinition = {
    org: {
        displayName: "Organization",
        sortOrder: 1,
    },
    advanced: {
        displayName: "Advanced",
        sortOrder: 2,
    },
};

enum fields {
    organizationName = "organizationName",
    description = "description",
    telephone = "telephone",
    faxNumber = "faxNumber",
    email = "email",
    addressLocality = "addressLocality",
    streetAddress = "streetAddress",
    postalCode = "postalCode",
    logoUrl = "logoUrl",
    contactUs = "contactUs",
    allProductsPage = "allProductsPage",
    socialLinks = "socialLinks",
}

export interface SeoFieldsProps {
    [fields.organizationName]: string;
    [fields.description]: string;
    [fields.telephone]: string;
    [fields.faxNumber]: string;
    [fields.email]: string;
    [fields.addressLocality]: string;
    [fields.streetAddress]: string;
    [fields.postalCode]: string;
    [fields.logoUrl]: string;
    [fields.contactUs]: LinkFieldValue;
    [fields.allProductsPage]: LinkFieldValue;
    [fields.socialLinks]: {
        fields: {
            url: string;
        };
    }[];
}

const createTextField = (name: keyof typeof fields, displayName: string, tab?: TabDefinition): FieldDefinition => ({
    name,
    displayName,
    editorTemplate: "TextField",
    defaultValue: "",
    fieldType: "General",
    tab,
});

const organizationFields: FieldDefinition[] = [
    createTextField(fields.organizationName, "Organization Name", tabs.org),
    createTextField(fields.description, "Organization Description", tabs.org),
    createTextField(fields.telephone, "Telephone", tabs.org),
    createTextField(fields.faxNumber, "Fax Number", tabs.org),
    createTextField(fields.email, "Email", tabs.org),
    createTextField(fields.addressLocality, "Address Locality", tabs.org),
    createTextField(fields.streetAddress, "Street Address", tabs.org),
    createTextField(fields.postalCode, "Postal Code", tabs.org),
    createTextField(fields.logoUrl, "Logo Url", tabs.org),
    {
        name: fields.contactUs,
        displayName: "Contact Us Url",
        editorTemplate: "LinkField",
        defaultValue: {
            type: "Page",
            value: "",
        },
        fieldType: "General",
        tab: tabs.org,
    },
];

const advancedFields: FieldDefinition[] = [
    {
        name: fields.allProductsPage,
        displayName: "All Products Page",
        editorTemplate: "LinkField",
        defaultValue: {
            type: "Page",
            value: "",
        },
        fieldType: "General",
        tab: tabs.advanced,
    },
    {
        name: fields.socialLinks,
        editorTemplate: "ListField",
        getDisplay: (item: HasFields) => {
            return item.fields.url || "";
        },
        defaultValue: [],
        fieldType: "General",
        fieldDefinitions: [
            {
                name: "url",
                displayName: "Social Url",
                editorTemplate: "TextField",
                defaultValue: "",
            },
        ],
        tab: tabs.advanced,
    },
];

const seoFieldDefinitions: FieldDefinition[] = [...organizationFields, ...advancedFields];

export default seoFieldDefinitions;
